/**
 * /security — security overview page. B2B trust signal. No SOC2 yet
 * (small team) — be honest about it; that gets more credibility than
 * fake badges.
 */
import { MarketingShell } from '../MarketingShell';

export function Security() {
  return (
    <MarketingShell
      currentLocale="en"
      hreflangPath="/security"
      documentTitle="Security at Draftlize · Draftlize"
      documentDescription="How Draftlize protects your data. Stateless containers on Fly.io, tenant-isolated Postgres, encrypted Tigris S3 for refs, no model training on your data, ever."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Security</span>
          <h1 className="serif" style={{ fontSize: 'clamp(40px, 5vw, 64px)', lineHeight: 1, margin: '12px 0 24px' }}>
            Security <em>at Draftlize.</em>
          </h1>
          <p className="lede">
            Plain-language summary of how we protect your data and what we don't do with it. No fake badges; small team; we're honest about the state we're in and the certifications we don't yet have.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">What we <em>do.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Tenant isolation</h3>
            <p>Every card, thread, and ref is keyed by <code>(tenant_id, project_id)</code> at the database level. Application code can't query across tenants — it's a schema-level guarantee, not a "we promise to filter" guarantee.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Stateless containers</h3>
            <p>The Draftlize server runs as stateless containers on Fly.io. No data on disk. Restart, redeploy, regional failover — nothing persists in compute. All state in Postgres + Tigris S3, both encrypted at rest.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">HTTPS-only + secure session cookies</h3>
            <p>HTTP/2 over TLS for every request. Session cookies are <code>HttpOnly</code>, <code>Secure</code>, <code>SameSite=Lax</code>, hashed bearer tokens at rest.</p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">No training on your data, ever</h3>
            <p>We don't train models on your cards, threads, or refs. Period. The LLM calls go to upstream providers (currently GLM, DeepSeek) under their no-training API agreements.</p>
          </div>
          <div className="problem-col">
            <span className="num">V</span>
            <h3 className="serif">Self-built error monitoring</h3>
            <p>No third-party error tracking (no Sentry, no Datadog). Errors are stored in our own database in the same tenant boundary. PII never leaves your tenant.</p>
          </div>
          <div className="problem-col">
            <span className="num">VI</span>
            <h3 className="serif">Daily backups to encrypted object storage</h3>
            <p>Postgres dumps daily to Tigris S3 bucket, encrypted at rest, 30-day retention. We restore-test monthly. Your data export endpoint pulls from the live DB anytime you ask.</p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">What we <em>don't have yet</em> (honest).</h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">A</span>
            <h3 className="serif">SOC 2</h3>
            <p>No SOC 2 Type II yet. We're a small team. If your procurement requires SOC 2 before signing, we're not the right tool for you right now. On the 12-month roadmap.</p>
          </div>
          <div className="problem-col">
            <span className="num">B</span>
            <h3 className="serif">GDPR DPA</h3>
            <p>Data Processing Agreement on request — email <a href="mailto:hi@draftlize.com">hi@draftlize.com</a>. Standard EU SCC template, signed within 48h.</p>
          </div>
          <div className="problem-col">
            <span className="num">C</span>
            <h3 className="serif">SSO / SAML</h3>
            <p>Not yet — currently GitHub OAuth + Google OAuth + email magic-link. SSO is on the roadmap when team plans land.</p>
          </div>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Incident response. <em>How we'd tell you.</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">If we discover a breach</h3>
            <p>Email to every affected user within 72 hours of confirmation. Public status post within 7 days. Plain-language details of what happened, what was exposed, what we changed.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">If you discover a vulnerability</h3>
            <p>Email <a href="mailto:security@draftlize.com">security@draftlize.com</a>. We respond within 48h. No bug bounty program yet (small team), but we'll acknowledge you publicly if the report is real and material.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">If you want to verify our claims</h3>
            <p>We'll do a 30-min call walking through the codebase architecture. Email <a href="mailto:hi@draftlize.com">hi@draftlize.com</a> if you're evaluating Draftlize for a meaningful spend and need to inspect.</p>
          </div>
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">More questions? <em>Email us.</em></h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            <a href="mailto:hi@draftlize.com">hi@draftlize.com</a> for general questions. <a href="mailto:security@draftlize.com">security@draftlize.com</a> for vulnerability reports. Real humans, fast responses.
          </p>
          <a className="btn-primary" href="/">Start free with $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
